import { erc20Abi } from "viem";
import { createPublicClient, http } from "viem";
import { arbitrum, arbitrumSepolia } from "viem/chains";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import { isMainnet } from "@/constants/flags.constant";
import type { HexString } from "@/types/general.types";

/**
 * Checks the BLESS token balance of a target contract on the parent chain (Arbitrum)
 * Used for safety validation before allowing bridging to Blessnet
 *
 * @param targetContractAddress - The contract address to check BLESS balance for
 * @returns Promise<bigint | null> - The BLESS balance in wei, or null if error
 */
export async function checkBlessBalanceOnParentChain(
  targetContractAddress: HexString
): Promise<bigint | null> {
  try {
    // Use Arbitrum mainnet or sepolia based on environment
    const chain = isMainnet ? arbitrum : arbitrumSepolia;

    console.log('🔍 [BLESSNET SAFETY] Checking BLESS balance on parent chain:');
    console.log('  - Target address:', targetContractAddress);
    console.log('  - Parent chain:', chain.name, `(ID: ${chain.id})`);
    console.log('  - BLESS token address:', CONTRACT_ADDRESSES.BLESS_TOKEN);
    console.log('  - Environment:', isMainnet ? 'mainnet' : 'testnet');

    // Create public client for the parent chain
    const publicClient = createPublicClient({
      chain,
      transport: http(),
    });

    // Read BLESS token balance
    const balance = await publicClient.readContract({
      address: CONTRACT_ADDRESSES.BLESS_TOKEN,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [targetContractAddress],
    });

    console.log('📊 [BLESSNET SAFETY] Balance check result:');
    console.log('  - Raw balance (wei):', balance.toString());
    console.log('  - Formatted balance (BLESS):', (Number(balance) / 10**18).toFixed(6));

    return balance;
  } catch (error) {
    console.error('❌ [BLESSNET SAFETY] Failed to check BLESS balance on parent chain:', error);
    return null;
  }
}

/**
 * Checks if the BLESS balance meets the minimum threshold requirement
 *
 * @param targetContractAddress - The contract address to check
 * @param minimumBalanceWei - The minimum required balance in wei
 * @returns Promise<boolean> - True if balance meets minimum, false otherwise
 */
export async function hasSufficientBlessBalance(
  targetContractAddress: HexString,
  minimumBalanceWei: bigint
): Promise<boolean> {
  console.log('🎯 [BLESSNET SAFETY] Starting balance validation:');
  console.log('  - Required minimum (wei):', minimumBalanceWei.toString());
  console.log('  - Required minimum (BLESS):', (Number(minimumBalanceWei) / 10**18).toFixed(6));

  const balance = await checkBlessBalanceOnParentChain(targetContractAddress);

  if (balance === null) {
    console.log('⚠️ [BLESSNET SAFETY] Balance check failed - blocking as safety measure');
    // If we can't check the balance, err on the side of caution
    return false;
  }

  const hasEnough = balance >= minimumBalanceWei;

  console.log('🔒 [BLESSNET SAFETY] Validation result:');
  console.log('  - Current balance:', (Number(balance) / 10**18).toFixed(6), 'BLESS');
  console.log('  - Required minimum:', (Number(minimumBalanceWei) / 10**18).toFixed(6), 'BLESS');
  console.log('  - Has sufficient balance:', hasEnough ? '✅ YES' : '❌ NO');
  console.log('  - Action:', hasEnough ? 'ALLOW bridging' : 'BLOCK bridging');

  return hasEnough;
}
